// components/ReplicaVideoPreview.tsx
import { useState, useRef, useEffect } from 'react';

interface ReplicaPreviewProps {
  previewUrl: string;
  isVisible: boolean;
  style?: React.CSSProperties;
}

export function ReplicaPreview({ previewUrl, isVisible, style }: ReplicaPreviewProps) {
  const previewRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isVisible || !previewRef.current) return;

    // First frame of thumbnail
    const setFirstFrame = () => {
      if (previewRef.current) {
        previewRef.current.currentTime = 0;
        previewRef.current.pause();
      }
    };

    // Set first frame if video was loaded
    if (previewRef.current.readyState >= 2) {
      setFirstFrame();
    } else {
      previewRef.current.addEventListener('loadeddata', setFirstFrame);
    }

    return () => {
      if (previewRef.current) {
        previewRef.current.removeEventListener('loadeddata', setFirstFrame);
      }
    };
  }, [isVisible]);

  const handleLoadStart = () => {
    setIsLoading(true);
    setHasError(false);
  };

  const handleLoadedData = () => {
    if (previewRef.current) {
      previewRef.current.currentTime = 0;
      previewRef.current.pause();
    }
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (!isVisible) return null;

  return (
    <div
      className='fixed bg-black rounded-lg shadow-2xl overflow-hidden pointer-events-none border border-gray-700'
      style={{
        width: '200px',
        height: '266px',
        zIndex: 100,
        ...style,
      }}
    >
      {/* Loader */}
      {isLoading && !hasError && (
        <div className='w-full h-full flex items-center justify-center bg-black'>
          <div className='flex flex-col items-center gap-3'>
            <div className='animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent'></div>
            <span className='text-sm text-white/80'>Loading preview...</span>
          </div>
        </div>
      )}

      {/* Error */}
      {hasError && (
        <div className='w-full h-full flex items-center justify-center bg-black'>
          <div className='text-center text-white/80'>
            <div className='text-sm mb-1'>Preview unavailable</div>
          </div>
        </div>
      )}

      {/* First frame display */}
      <video
        ref={previewRef}
        src={previewUrl}
        className={`w-full h-full object-cover ${isLoading || hasError ? 'hidden' : 'block'}`}
        muted
        preload='metadata'
        onLoadStart={handleLoadStart}
        onLoadedData={handleLoadedData}
        onError={handleError}
      />
    </div>
  );
}
